import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy, type CtfHudState } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/** Captures needed to win a CTF round — the denominator in the "X/3" score. */
const CTF_CAPTURES_TO_WIN = 3;

/**
 * Game Mode Status - Displays game-mode-specific HUD info
 *
 * Centered at the top of the game screen, below game-stats (on portrait
 * phones, a row lower, under the touch toolbar):
 * - CTF: Flag icons with carrier names and capture scores (X/3)
 * - BTR: "N players alive" text
 * - FFA: Nothing shown
 *
 * @element game-mode-status
 */
@customElement('game-mode-status')
export class GameModeStatus extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        position: absolute;
        left: 50%;
        width: 440px;
        margin-left: -220px;
        /* + the status-bar inset, in step with the top bar (see airmash-game). */
        top: calc(50px + env(safe-area-inset-top, 0px));
        white-space: nowrap;
        text-align: center;
        /* Declared here rather than inherited from the body — the component
           owns its own typography. Click-through, so no selection or tap rules
           are needed. */
        font-family: var(--font-family);
        font-size: var(--font-size-sm);
        text-shadow: var(--shadow-text);
        color: var(--color-text-tooltip);
        cursor: default;
        pointer-events: none;
      }

      /* A flag either side of centre, each carrier label outboard of its flag. */
      .blueflag,
      .redflag {
        position: absolute;
        top: 0;
        width: 32px;
        height: 32px;
        background-repeat: no-repeat;
        background-size: 32px 32px;
      }

      .blueflag {
        right: calc(50% + 12px);
        background-image: url('/vanilla/ui/gameplay/ctf_flag_blue.png');
      }

      .redflag {
        left: calc(50% + 12px);
        background-image: url('/vanilla/ui/gameplay/ctf_flag_red.png');
      }

      .blueflag-player,
      .redflag-player {
        position: absolute;
        top: 2px;
      }

      .blueflag-player {
        right: calc(50% + 42px);
        text-align: right;
      }

      .redflag-player {
        left: calc(50% + 42px);
        text-align: left;
      }

      .blueflag-player,
      .redflag-player,
      .playersalive {
        color: var(--color-text-white);
        font-size: var(--font-size-16);
        font-weight: bold;
      }

      .rounds {
        display: inline-block;
        width: 50px;
        text-align: center;
        background-color: var(--color-bg-chip);
        border-radius: var(--radius-md);
        padding: 4px 0;
        margin-left: 12px;
        margin-right: 12px;
      }

      .divider {
        margin-left: 4px;
        margin-right: 4px;
        color: var(--color-text-tertiary);
      }

      @media only screen and (max-width: 599px) and (orientation: portrait),
        only screen and (max-height: 599px) and (orientation: landscape) {
        :host {
          transform: scale(0.6);
        }
      }

      /* Portrait phones: our 44px touch toolbar ends at 58px, where legacy had
         nothing, so at 50px the strip printed over the buttons. It drops a
         row, level with the chat button and the ping readout, and carrier
         names are capped so a long one stops short of the ping readout (about
         7 characters a side on a 390px phone). */
      @media only screen and (max-width: 599px) and (orientation: portrait) {
        :host {
          top: calc(64px + env(safe-area-inset-top, 0px));
        }

        .carrier {
          display: inline-block;
          max-width: 74px;
          overflow: hidden;
          text-overflow: ellipsis;
          vertical-align: bottom;
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  override render() {
    const { gameType, ctfHud, btrPlayersAlive } = this.uiState;

    // Every mode is a case, so a new one fails to compile until someone
    // decides whether it shows a status.
    switch (gameType) {
      case 'ctf':
        return ctfHud ? this.renderCtf(ctfHud) : nothing;
      case 'btr':
        return btrPlayersAlive != null && btrPlayersAlive > 1
          ? html`<div class="playersalive">${btrPlayersAlive} players alive</div>`
          : nothing;
      case 'ffa':
      case null:
        return nothing;
    }
  }

  private renderCtf(ctfHud: CtfHudState) {
    const { blue, red } = ctfHud;

    return html`
      <div class="blueflag"></div>
      <div class="blueflag-player">
        ${this.renderCarrier(blue.carrierName)}<span class="rounds"
          >${blue.captures}<span class="divider">/</span>${CTF_CAPTURES_TO_WIN}</span
        >
      </div>
      <div class="redflag"></div>
      <div class="redflag-player">
        <span class="rounds">${red.captures}<span class="divider">/</span>${CTF_CAPTURES_TO_WIN}</span
        >${this.renderCarrier(red.carrierName)}
      </div>
    `;
  }

  /** The carrier's name in its own span, so portrait phones can cap it. */
  private renderCarrier(name: string | null) {
    return name === null ? nothing : html`<span class="carrier">${name}</span>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'game-mode-status': GameModeStatus;
  }
}
